import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import {
  Activity,
  Building2,
  Car,
  CheckCircle2,
  Eye,
  EyeOff,
  ExternalLink,
  LocateFixed,
  MapPin,
  Navigation,
  Search,
  ShieldCheck,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import SEO from '../../components/SEO/SEO';
import { useAuth } from '../../contexts/AuthContext';
import ridesService, {
  CreateRidePayload,
  CreateRideStandPayload,
  CreateRideVehiclePayload,
  PaymentMode,
  RegisterRideDriverPayload,
  RideCategory,
  RideDriverPlan,
  RideLocation,
  RIDES_API_URL,
  StandQueueModel,
  StandType,
} from '../../services/rides.service';

const SITE_URL = 'https://localally.in';
const RIDES_HERO_IMAGE =
  'https://images.unsplash.com/photo-1518611507436-f9221403cca2?auto=format&fit=crop&w=1800&q=80';

const rideCategoryOptions: RideCategory[] = ['AUTO', 'BIKE', 'CAB', 'VAN', 'SHARED_RIDE'];
const paymentOptions: PaymentMode[] = ['CASH', 'UPI', 'QR'];
const standTypeOptions: StandType[] = [
  'AUTO_STAND',
  'BIKE_STAND',
  'CAB_STAND',
  'SHARED_AUTO_POINT',
  'VILLAGE_TRANSPORT_POINT',
  'SCHOOL_TRANSPORT_POINT',
  'HOSPITAL_TRANSPORT_POINT',
  'EVENT_TEMPORARY_STAND',
];
const queueOptions: StandQueueModel[] = ['FIFO', 'MANUAL', 'OPEN_ACCEPTANCE', 'ROUTE_BASED', 'PRIORITY'];
const LEAFLET_CSS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const DEFAULT_MAP_CENTER: [number, number] = [17.385044, 78.486671];
const PENDING_RIDES_DRIVER_PLAN_KEY = 'pendingRidesDriverPlanPurchase';
const DRIVER_REQUEST_POLL_MS = 15000;
let leafletLoadPromise: Promise<LeafletApi> | null = null;

type MessageState = {
  type: 'success' | 'error';
  text: string;
} | null;

type LeafletApi = any;
type DriverDocumentKey =
  | 'licenceDocument'
  | 'identityDocument'
  | 'vehicleRcDocument'
  | 'vehicleInsuranceDocument'
  | 'permitDocument';

const driverDocumentLabels: Record<DriverDocumentKey, string> = {
  licenceDocument: 'Driving Licence',
  identityDocument: 'Identity Proof',
  vehicleRcDocument: 'Vehicle RC',
  vehicleInsuranceDocument: 'Vehicle Insurance',
  permitDocument: 'Permit / Fitness',
};

const driverDocumentUrlFields: Record<DriverDocumentKey, keyof RegisterRideDriverPayload> = {
  licenceDocument: 'licenceDocumentUrl',
  identityDocument: 'identityDocumentUrl',
  vehicleRcDocument: 'vehicleRcDocumentUrl',
  vehicleInsuranceDocument: 'vehicleInsuranceDocumentUrl',
  permitDocument: 'permitDocumentUrl',
};

declare global {
  interface Window {
    L?: LeafletApi;
  }
}

type RidesAccountPayload = {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  phone: string;
  password: string;
  confirmPassword: string;
};

const createAccountPayload = (): RidesAccountPayload => ({
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  phone: '',
  password: '',
  confirmPassword: '',
});

const createLocation = (): RideLocation => ({
  address: '',
  landmark: '',
  village: '',
  mandal: '',
  district: '',
  state: '',
});

const formatEnum = (value?: string) =>
  String(value || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getRecordId = (item: any) => {
  if (!item) return '';
  if (typeof item.id === 'string') return item.id;
  if (typeof item._id === 'string') return item._id;
  if (typeof item._id?.$oid === 'string') return item._id.$oid;
  if (typeof item._id?.toString === 'function') return item._id.toString();
  return '';
};

const parseNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && value.trim() ? parsed : undefined;
};

const compactRecord = (record: Record<string, any>) =>
  Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== ''));

const normalizePhone = (phone: string) =>
  phone.trim().startsWith('+') ? `+${phone.replace(/\D/g, '')}` : phone.trim();

const getStoredAccessToken = () => {
  try {
    const raw = localStorage.getItem('user');
    const parsed = raw ? JSON.parse(raw) : null;
    return String(parsed?.accessToken || parsed?.user?.accessToken || '').trim();
  } catch {
    return '';
  }
};

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

const toDateTimeLocalValue = (date: Date) => {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const fromDateTimeLocalValue = (value: string) => (value ? new Date(value).toISOString() : undefined);

const formatDateTime = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

const formatCurrency = (amount?: number, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

const hasDriverDocument = (documentSummary: Record<string, string> | undefined, key: DriverDocumentKey) =>
  Boolean(documentSummary?.[driverDocumentUrlFields[key]]);

const parseCoordinate = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && value.trim() ? parsed : undefined;
};

const hasCoordinates = (location?: Partial<RideLocation>) =>
  Number.isFinite(Number(location?.latitude)) && Number.isFinite(Number(location?.longitude));

const buildLocationQuery = (location?: Partial<RideLocation>) => {
  if (!location) return '';
  if (hasCoordinates(location)) return `${location.latitude},${location.longitude}`;
  const addressParts = [
    location.address,
    location.landmark,
    location.village,
    location.mandal,
    location.district,
  ].filter(Boolean);
  if (addressParts.length === 0) return '';
  return [
    ...addressParts,
    location.state,
  ]
    .filter(Boolean)
    .join(', ');
};

const buildGoogleMapsUrl = (location?: Partial<RideLocation>) => {
  const query = buildLocationQuery(location);
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '';
};

const buildGoogleDirectionsUrl = (pickup?: Partial<RideLocation>, destination?: Partial<RideLocation>) => {
  const origin = buildLocationQuery(pickup);
  const destinationQuery = buildLocationQuery(destination);
  if (!origin || !destinationQuery) return '';
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destinationQuery)}&travelmode=driving`;
};

const parseCoordinatesFromMapText = (input: string): Pick<RideLocation, 'latitude' | 'longitude'> | null => {
  const text = decodeURIComponent(input || '').replace(/[@?&=]/g, ' ');
  const match = text.match(/(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)/);
  if (!match) return null;
  const latitude = Number(match[1]);
  const longitude = Number(match[2]);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return { latitude, longitude };
};

const getGeolocationMessage = (error: unknown) => {
  const code = typeof error === 'object' && error && 'code' in error ? Number((error as GeolocationPositionError).code) : 0;
  if (code === 1) return 'Location permission was denied.';
  if (code === 2) return 'Current location is unavailable.';
  if (code === 3) return 'Location request timed out.';
  return 'Unable to capture current location.';
};

const getBrowserRideLocation = (address = 'Current GPS location'): Promise<RideLocation> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported in this browser.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          address,
          latitude: Number(position.coords.latitude.toFixed(6)),
          longitude: Number(position.coords.longitude.toFixed(6)),
        });
      },
      (error) => reject(error),
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 30000,
      },
    );
  });

const loadLeaflet = (): Promise<LeafletApi> => {
  if (typeof window === 'undefined') return Promise.reject(new Error('Map picker is unavailable during server rendering.'));
  if (window.L) return Promise.resolve(window.L);
  if (leafletLoadPromise) return leafletLoadPromise;

  leafletLoadPromise = new Promise((resolve, reject) => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS_URL;
      document.head.appendChild(link);
    }

    const existingScript = document.getElementById('leaflet-js') as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', () => (window.L ? resolve(window.L) : reject(new Error('Map library did not load.'))), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Unable to load map library.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'leaflet-js';
    script.src = LEAFLET_JS_URL;
    script.async = true;
    script.onload = () => (window.L ? resolve(window.L) : reject(new Error('Map library did not load.')));
    script.onerror = () => reject(new Error('Unable to load map library.'));
    document.body.appendChild(script);
  });

  return leafletLoadPromise;
};

const resolveApiErrorMessage = (error: any, fallback: string): string => {
  const payload = error?.response?.data;
  const candidate = payload?.message || payload?.error || error?.message;
  if (typeof candidate === 'string' && candidate.trim()) return candidate;
  if (Array.isArray(candidate)) return candidate.join(' ');
  return fallback;
};

const isAccountConflictError = (error: any) => {
  const payload = error?.response?.data;
  const message = payload?.message?.message || payload?.message || error?.message || '';
  return error?.response?.status === 409 || String(message).toLowerCase().includes('already in use');
};

const ridesSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'LocalAlly Rides',
  serviceType: 'Local auto, bike taxi, cab, stand and shared transport discovery',
  provider: {
    '@type': 'Organization',
    name: 'LocalAlly',
    url: SITE_URL,
  },
  areaServed: [
    { '@type': 'AdministrativeArea', name: 'Andhra Pradesh' },
    { '@type': 'AdministrativeArea', name: 'Telangana' },
    { '@type': 'Country', name: 'India' },
  ],
  url: `${SITE_URL}/rides`,
};

const ridesNavLinks = [
  { to: '/rides/dashboard', label: 'Dashboard', icon: Activity },
  { to: '/rides/book', label: 'Book Ride', icon: Navigation },
  { to: '/rides/register', label: 'Register', icon: UserPlus },
  { to: '/rides/driver/register', label: 'Driver', icon: Car },
  { to: '/rides/stands', label: 'Stands', icon: Building2 },
  { to: '/rides/stand/register', label: 'Register Stand', icon: Users },
];

const sectionClass = (maxWidth = 'max-w-7xl', extra = '') =>
  ['mx-auto', maxWidth, 'px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10', extra].filter(Boolean).join(' ');

const panelClass = (extra = '') =>
  ['rounded-lg border border-slate-200 bg-white/95 p-4 shadow-[0_18px_48px_-36px_rgba(15,23,42,0.28)] sm:p-6', extra]
    .filter(Boolean)
    .join(' ');

const PageShell: React.FC<{
  title: string;
  description: string;
  path: string;
  robots?: string;
  children: React.ReactNode;
}> = ({ title, description, path, robots, children }) => {
  const location = useLocation();
  const isActiveLink = (to: string, label: string) =>
    location.pathname === to ||
    location.pathname.startsWith(`${to}/`) ||
    (label === 'Register' && location.pathname.startsWith('/rides/register'));

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f8fafc_0%,_#eefdf5_42%,_#f8fafc_100%)] text-slate-950">
      <SEO title={`${title} | LocalAlly`} description={description} path={path} image={RIDES_HERO_IMAGE} robots={robots} schema={ridesSchema} />
      <section
        className="relative overflow-hidden border-b border-emerald-900/20 bg-slate-950 text-white"
        style={{ backgroundImage: `linear-gradient(90deg, rgba(2,6,23,0.94), rgba(6,78,59,0.78), rgba(14,116,144,0.38)), url(${RIDES_HERO_IMAGE})`, backgroundPosition: 'center', backgroundSize: 'cover' }}
      >
        <div className={sectionClass('max-w-7xl', 'relative py-7 sm:py-9 lg:py-10')}>
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-4xl">
              <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase text-emerald-100 backdrop-blur">
                <Car className="h-4 w-4" aria-hidden="true" />
                LocalAlly Rides
              </p>
              <h1 className="mt-4 max-w-4xl text-2xl font-semibold leading-8 sm:text-4xl sm:leading-tight">{title}</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-100 sm:text-base sm:leading-7">{description}</p>
            </div>
            <Link
              to="/rides/book"
              className="inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-slate-950/15 transition hover:bg-emerald-50 sm:w-auto"
            >
              <Navigation className="h-4 w-4" aria-hidden="true" />
              Book Ride
            </Link>
          </div>
          <nav className="-mx-4 mt-6 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0" aria-label="Rides module">
            {ridesNavLinks.map(({ to, label, icon: Icon }) => {
              const active = isActiveLink(to, label);
              return (
                <Link
                  key={to}
                  to={to}
                  className={`inline-flex min-h-[42px] flex-none items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                    active
                      ? 'border-white bg-white text-slate-950 shadow-lg shadow-slate-950/15'
                      : 'border-white/20 bg-white/10 text-white backdrop-blur hover:bg-white/15'
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </Link>
              );
            })}
          </nav>
        </div>
      </section>
      {children}
    </div>
  );
};

const Message: React.FC<{ state: MessageState }> = ({ state }) => {
  if (!state) return null;
  return (
    <div className={`rounded-lg border px-4 py-3 text-sm leading-6 ${state.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'}`}>
      {state.text}
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  placeholder?: string;
}> = ({ label, value, onChange, required, type = 'text', placeholder }) => (
  <label className="block text-sm font-semibold text-slate-700">
    {label}
    {required && <span className="ml-1 text-rose-600">*</span>}
    <input
      type={type}
      value={value}
      required={required}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className="mt-2 min-h-[48px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
    />
  </label>
);

const PasswordField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}> = ({ label, value, onChange, required }) => {
  const inputId = useId();
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;
  const actionLabel = `${visible ? 'Hide' : 'Show'} ${label.toLowerCase()}`;

  return (
    <div className="block text-sm font-semibold text-slate-700">
      <label htmlFor={inputId}>
        {label}
        {required && <span className="ml-1 text-rose-600">*</span>}
      </label>
      <div className="relative mt-2">
        <input
          id={inputId}
          type={visible ? 'text' : 'password'}
          value={value}
          required={required}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-[48px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pr-12 text-sm text-slate-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
        />
        <button
          type="button"
          aria-label={actionLabel}
          title={actionLabel}
          onClick={() => setVisible((current) => !current)}
          className="absolute inset-y-0 right-2 my-auto flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-emerald-700 focus:outline-none focus:ring-4 focus:ring-emerald-100"
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const SelectField: React.FC<{
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  required?: boolean;
}> = ({ label, value, options, onChange, required }) => (
  <label className="block text-sm font-semibold text-slate-700">
    {label}
    {required && <span className="ml-1 text-rose-600">*</span>}
    <select
      value={value}
      required={required}
      onChange={(event) => onChange(event.target.value)}
      className="mt-2 min-h-[48px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
    >
      <option value="">Select</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {formatEnum(option)}
        </option>
      ))}
    </select>
  </label>
);

const TextAreaField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}> = ({ label, value, onChange, required }) => (
  <label className="block text-sm font-semibold text-slate-700">
    {label}
    {required && <span className="ml-1 text-rose-600">*</span>}
    <textarea
      value={value}
      required={required}
      rows={4}
      onChange={(event) => onChange(event.target.value)}
      className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
    />
  </label>
);

const CheckboxField: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <label className="flex min-h-[44px] items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-emerald-300 hover:bg-emerald-50/40">
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
    />
    {label}
  </label>
);

const DocumentUploadField: React.FC<{
  label: string;
  file?: File;
  existingUrl?: string;
  required?: boolean;
  onChange: (file?: File) => void;
}> = ({ label, file, existingUrl, required, onChange }) => (
  <label className="block rounded-lg border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-700">
    <span>
      {label}
      {required && <span className="ml-1 text-rose-600">*</span>}
    </span>
    <input
      type="file"
      accept="image/*,application/pdf"
      required={required && !file && !existingUrl}
      onChange={(event) => onChange(event.target.files?.[0])}
      className="mt-3 block w-full text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-slate-950 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-emerald-700"
    />
    <span className="mt-2 block text-xs font-medium leading-5 text-slate-500">
      {file
        ? `${file.name} (${Math.ceil(file.size / 1024)} KB)`
        : existingUrl
          ? 'Already uploaded. Choose a file only if you want to replace it.'
          : 'Upload PDF, JPG, or PNG. Max 5 MB.'}
    </span>
    {existingUrl && !file && (
      <a
        href={existingUrl}
        target="_blank"
        rel="noreferrer"
        className="mt-2 inline-flex text-xs font-semibold text-emerald-700 underline"
      >
        View uploaded file
      </a>
    )}
    {file && (
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          onChange(undefined);
        }}
        className="mt-2 text-xs font-semibold text-rose-600 transition hover:text-rose-700"
      >
        Remove file
      </button>
    )}
  </label>
);

const MapPicker: React.FC<{
  value: RideLocation;
  prefix: string;
  onSelect: (coordinates: Pick<RideLocation, 'latitude' | 'longitude'>) => void;
}> = ({ value, prefix, onSelect }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const onSelectRef = useRef(onSelect);
  const initialCoordinatesRef = useRef<Pick<RideLocation, 'latitude' | 'longitude'> | null>(
    hasCoordinates(value)
      ? {
          latitude: Number(value.latitude),
          longitude: Number(value.longitude),
        }
      : null,
  );
  const selectedLatitude = value.latitude;
  const selectedLongitude = value.longitude;
  const [error, setError] = useState('');

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    let cancelled = false;

    const startMap = async () => {
      if (!containerRef.current) return;
      try {
        const L = await loadLeaflet();
        if (cancelled || !containerRef.current) return;

        const initialCoordinates = initialCoordinatesRef.current;
        const center: [number, number] = initialCoordinates
          ? [Number(initialCoordinates.latitude), Number(initialCoordinates.longitude)]
          : DEFAULT_MAP_CENTER;
        const map = L.map(containerRef.current).setView(center, initialCoordinates ? 16 : 12);
        mapRef.current = map;

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
          maxZoom: 19,
        }).addTo(map);

        const placeMarker = (latitude: number, longitude: number) => {
          if (markerRef.current) {
            markerRef.current.setLatLng([latitude, longitude]);
            return;
          }
          markerRef.current = L.marker([latitude, longitude]).addTo(map);
        };

        if (initialCoordinates) {
          placeMarker(Number(initialCoordinates.latitude), Number(initialCoordinates.longitude));
        }

        map.on('click', (event: any) => {
          const latitude = Number(event.latlng.lat.toFixed(6));
          const longitude = Number(event.latlng.lng.toFixed(6));
          placeMarker(latitude, longitude);
          onSelectRef.current({ latitude, longitude });
        });

        window.setTimeout(() => map.invalidateSize(), 80);
      } catch (loadError: any) {
        if (!cancelled) setError(loadError?.message || 'Unable to load map picker.');
      }
    };

    void startMap();

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.L) return;
    const latitude = Number(selectedLatitude);
    const longitude = Number(selectedLongitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      return;
    }
    if (markerRef.current) {
      markerRef.current.setLatLng([latitude, longitude]);
    } else {
      markerRef.current = window.L.marker([latitude, longitude]).addTo(mapRef.current);
    }
    mapRef.current.setView([latitude, longitude], Math.max(mapRef.current.getZoom(), 15));
  }, [selectedLatitude, selectedLongitude]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-semibold text-slate-800">{prefix} map picker</p>
        <p className="text-xs font-medium text-slate-500">Click the map to pin this location.</p>
      </div>
      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div>
      ) : (
        <div ref={containerRef} className="h-72 w-full overflow-hidden rounded-lg bg-slate-100" />
      )}
    </div>
  );
};

const LocationFields: React.FC<{
  value: RideLocation;
  onChange: (value: RideLocation) => void;
  prefix?: string;
  required?: boolean;
}> = ({ value, onChange, prefix = 'Location', required }) => {
  const [mapInput, setMapInput] = useState('');
  const [locating, setLocating] = useState(false);
  const [status, setStatus] = useState<MessageState>(null);
  const hasLocalityDetails = Boolean(value.village || value.mandal || value.district || value.state);
  const [showLocalityFields, setShowLocalityFields] = useState(hasLocalityDetails);
  const update = (patch: Partial<RideLocation>) => onChange(compactRecord({ ...value, ...patch }) as RideLocation);
  const mapUrl = buildGoogleMapsUrl(value);
  const coordinatesAvailable = hasCoordinates(value);
  const [showMapPicker, setShowMapPicker] = useState(coordinatesAvailable);

  useEffect(() => {
    if (hasLocalityDetails) setShowLocalityFields(true);
  }, [hasLocalityDetails]);

  useEffect(() => {
    if (coordinatesAvailable) setShowMapPicker(true);
  }, [coordinatesAvailable]);

  const captureCurrentLocation = async () => {
    setLocating(true);
    setStatus(null);
    try {
      const currentLocation = await getBrowserRideLocation(`${prefix} GPS location`);
      onChange(compactRecord({ ...value, ...currentLocation, address: value.address || currentLocation.address }) as RideLocation);
      setStatus({ type: 'success', text: `${prefix} map location captured.` });
    } catch (error) {
      setStatus({ type: 'error', text: getGeolocationMessage(error) });
    } finally {
      setLocating(false);
    }
  };

  const applyMapInput = () => {
    const coordinates = parseCoordinatesFromMapText(mapInput);
    if (!coordinates) {
      setStatus({ type: 'error', text: 'Enter coordinates or paste a map link that contains latitude and longitude.' });
      return;
    }
    update(coordinates);
    setMapInput('');
    setStatus({ type: 'success', text: `${prefix} coordinates added.` });
  };

  const selectMapLocation = (coordinates: Pick<RideLocation, 'latitude' | 'longitude'>) => {
    update({
      ...coordinates,
      address: value.address?.trim() ? value.address : `${prefix} pinned location`,
    });
    setShowMapPicker(true);
    setStatus({ type: 'success', text: `${prefix} map pin selected.` });
  };

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-950">{prefix} Location</h2>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            {coordinatesAvailable ? `Pinned at ${value.latitude}, ${value.longitude}` : 'Add an address, capture GPS, paste coordinates, or pick on the map.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowMapPicker((current) => !current)}
            className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100"
          >
            <MapPin className="h-4 w-4" aria-hidden="true" />
            {showMapPicker ? 'Hide Map Picker' : 'Pick on Map'}
          </button>
          <button
            type="button"
            onClick={() => void captureCurrentLocation()}
            disabled={locating}
            className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <LocateFixed className="h-4 w-4" aria-hidden="true" />
            {locating ? 'Locating...' : 'Use Current Location'}
          </button>
          {mapUrl && (
            <a
              href={mapUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              Open Map
            </a>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label={`${prefix} Address / Search`} value={value.address || ''} required={required} onChange={(address) => update({ address })} />
        <Field label="Landmark" value={value.landmark || ''} onChange={(landmark) => update({ landmark })} />
      </div>

      <button
        type="button"
        onClick={() => setShowLocalityFields((current) => !current)}
        className="text-sm font-semibold text-emerald-700 transition hover:text-emerald-900"
      >
        {showLocalityFields ? 'Hide locality details' : 'Add locality details'}
      </button>

      {showLocalityFields && (
        <div className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-2">
          <Field label="Village / Area" value={value.village || ''} onChange={(village) => update({ village })} />
          <Field label="Mandal" value={value.mandal || ''} onChange={(mandal) => update({ mandal })} />
          <Field label="District" value={value.district || ''} onChange={(district) => update({ district })} />
          <Field label="State" value={value.state || ''} onChange={(state) => update({ state })} />
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_160px_160px_auto] md:items-end">
        <Field
          label="Map Link / Coordinates"
          value={mapInput}
          placeholder="17.385044,78.486671 or Google Maps link"
          onChange={setMapInput}
        />
        <Field
          label="Latitude"
          value={value.latitude !== undefined ? String(value.latitude) : ''}
          onChange={(latitude) => update({ latitude: parseCoordinate(latitude) })}
        />
        <Field
          label="Longitude"
          value={value.longitude !== undefined ? String(value.longitude) : ''}
          onChange={(longitude) => update({ longitude: parseCoordinate(longitude) })}
        />
        <button
          type="button"
          onClick={applyMapInput}
          className="inline-flex min-h-[48px] items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50"
        >
          Apply
        </button>
      </div>

      {coordinatesAvailable && (
        <button
          type="button"
          onClick={() => update({ latitude: undefined, longitude: undefined })}
          className="text-sm font-semibold text-slate-500 transition hover:text-rose-600"
        >
          Clear pinned coordinates
        </button>
      )}

      {status && <Message state={status} />}

      {showMapPicker && <MapPicker value={value} prefix={prefix} onSelect={selectMapLocation} />}
    </div>
  );
};

const PrimaryButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, className = '', ...props }) => (
  <button
    {...props}
    className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-emerald-700 px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_32px_-24px_rgba(4,120,87,0.9)] transition hover:bg-emerald-800 focus:outline-none focus:ring-4 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
  >
    {children}
  </button>
);

const SecondaryButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, className = '', ...props }) => (
  <button
    {...props}
    className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50 focus:outline-none focus:ring-4 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
  >
    {children}
  </button>
);

const StatusPill: React.FC<{ value?: string }> = ({ value }) => {
  const normalized = String(value || '').toUpperCase();
  const color =
    normalized.includes('APPROVED') || normalized.includes('ACTIVE') || normalized.includes('COMPLETED')
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : normalized.includes('REJECTED') || normalized.includes('CANCELLED') || normalized.includes('BLOCKED')
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : 'border-amber-200 bg-amber-50 text-amber-700';
  return <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${color}`}>{formatEnum(value)}</span>;
};

const Stat: React.FC<{ title: string; value: string | number }> = ({ title, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-[0_16px_42px_-36px_rgba(15,23,42,0.3)] sm:p-5">
    <p className="text-xs font-semibold uppercase text-slate-500">{title}</p>
    <p className="mt-2 break-words text-2xl font-semibold leading-8 text-slate-950">{value}</p>
  </div>
);

const DriverDocumentLinks: React.FC<{ documentSummary?: Record<string, string> }> = ({ documentSummary }) => {
  const links = [
    ['licenceDocumentUrl', 'Driving Licence'],
    ['identityDocumentUrl', 'Identity Proof'],
    ['vehicleRcDocumentUrl', 'Vehicle RC'],
    ['vehicleInsuranceDocumentUrl', 'Insurance'],
    ['permitDocumentUrl', 'Permit / Fitness'],
  ].filter(([key]) => Boolean(documentSummary?.[key]));

  if (!links.length) {
    return <p className="mt-3 text-sm text-amber-700">No uploaded documents found.</p>;
  }

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {links.map(([key, label]) => (
        <a
          key={key}
          href={documentSummary?.[key]}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-[36px] items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-100"
        >
          {label}
        </a>
      ))}
    </div>
  );
};

const getDriverMissingItems = (driver?: any, vehicles: any[] = []) => {
  if (!driver) return ['Driver profile'];
  const documentSummary = driver.documentSummary || {};
  const missing = [
    !driver.fullName && 'Full name',
    !driver.mobileNumber && 'Mobile number',
    !(Array.isArray(driver.categories) && driver.categories.length) && 'Driver categories',
    !driver.currentAddress?.address && 'Current address',
    !documentSummary.licenceNumber && 'Licence number',
    !documentSummary.licenceDocumentUrl && 'Driving licence upload',
    !documentSummary.identityDocumentUrl && 'Identity proof upload',
    driver.billingSummary?.status !== 'ACTIVE' && 'Active driver subscription',
    !vehicles.length && 'Vehicle details',
    vehicles.length > 0 && !vehicles.some((vehicle) => vehicle.status === 'ACTIVE') && 'Admin-approved active vehicle',
  ].filter(Boolean);
  return missing as string[];
};

const DetailItem: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <div className="mt-1 break-words text-sm font-semibold leading-6 text-slate-900">{value}</div>
    </div>
  );
};

const DriverVerificationDetails: React.FC<{ driver?: any; vehicles?: any[]; showAvailability?: boolean }> = ({ driver, vehicles = [], showAvailability }) => {
  if (!driver) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
        Driver registration is not submitted yet.
      </div>
    );
  }

  const documentSummary = driver.documentSummary || {};
  const payoutDetails = driver.payoutDetails || {};
  const billingSummary = driver.billingSummary || {};
  const missingItems = getDriverMissingItems(driver, vehicles);
  const address = [
    driver.currentAddress?.address,
    driver.currentAddress?.landmark,
    driver.currentAddress?.village,
    driver.currentAddress?.mandal,
    driver.currentAddress?.district,
    driver.currentAddress?.state,
  ].filter(Boolean).join(', ');
  const availabilityLocation = buildLocationQuery(driver.currentLocation);
  const availabilityMapUrl = buildGoogleMapsUrl(driver.currentLocation);
  const availabilityPost = driver.availabilityPost || {};

  return (
    <div className="mt-4 space-y-4">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <DetailItem label="Verification" value={<StatusPill value={driver.verificationStatus} />} />
        <DetailItem label="Driver Plan" value={billingSummary.planName ? `${billingSummary.planName} (${formatCurrency(Number(billingSummary.price || 0), String(billingSummary.currency || 'INR'))})` : 'Not selected'} />
        <DetailItem label="Billing" value={<StatusPill value={String(billingSummary.status || 'PENDING_PAYMENT')} />} />
        <DetailItem label="Plan Expires" value={formatDateTime(String(billingSummary.expiresAt || ''))} />
        <DetailItem label="Mobile" value={driver.mobileNumber} />
        <DetailItem label="Categories" value={(driver.categories || []).map(formatEnum).join(', ')} />
        <DetailItem label="Service Areas" value={(driver.serviceAreas || []).join(', ')} />
        <DetailItem label="Preferred Language" value={driver.preferredLanguage} />
        <DetailItem label="Licence Number" value={documentSummary.licenceNumber} />
        <DetailItem label="Identity Reference" value={documentSummary.identityDocument} />
        <DetailItem label="UPI" value={payoutDetails.upiId} />
        <DetailItem label="Address" value={address} />
        {showAvailability && (
          <>
            <DetailItem label="Online" value={driver.isOnline ? 'Yes' : 'No'} />
            <DetailItem label="Available" value={driver.isAvailable ? 'Yes' : 'No'} />
            <DetailItem
              label="Availability"
              value={[
                availabilityPost.note,
                availabilityPost.validUntil ? `Until ${formatDateTime(availabilityPost.validUntil)}` : '',
                availabilityLocation,
              ].filter(Boolean).join(' | ')}
            />
          </>
        )}
      </div>

      {showAvailability && availabilityMapUrl && (
        <a
          href={availabilityMapUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50"
        >
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
          Driver Availability Map
        </a>
      )}

      <DriverDocumentLinks documentSummary={documentSummary} />

      {driver.adminRemarks && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
          <span className="font-semibold">Admin remarks:</span> {driver.adminRemarks}
        </div>
      )}

      {missingItems.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
          <span className="font-semibold">Missing before full activation:</span> {missingItems.join(', ')}
        </div>
      )}

      {vehicles.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {vehicles.map((vehicle) => (
            <div key={getRecordId(vehicle) || vehicle.registrationNumber} className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-950">{vehicle.registrationNumber}</p>
                  <p className="mt-1 text-slate-600">{formatEnum(vehicle.vehicleType)} {[vehicle.manufacturer, vehicle.model].filter(Boolean).join(' ')}</p>
                </div>
                <StatusPill value={vehicle.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DriverPlanCards: React.FC<{
  plans: RideDriverPlan[];
  selectedPlanId?: string;
  activePlanId?: string;
  onSelect?: (planId: string) => void;
  actionLabel?: (plan: RideDriverPlan) => string;
  onAction?: (plan: RideDriverPlan) => void;
  disabled?: boolean;
}> = ({ plans, selectedPlanId, activePlanId, onSelect, actionLabel, onAction, disabled }) => {
  if (!plans.length) {
    return <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">Driver plans are loading.</div>;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      {plans.map((plan) => {
        const selected = selectedPlanId === plan.id;
        const active = activePlanId === plan.id;
        return (
          <article
            key={plan.id}
            className={`rounded-lg border bg-white p-4 shadow-sm transition sm:p-5 ${
              selected || active ? 'border-emerald-300 ring-4 ring-emerald-100' : 'border-slate-200'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-950">{plan.name}</h3>
                <p className="mt-1 text-sm leading-6 text-slate-600">{plan.description}</p>
              </div>
              {(selected || active) && <StatusPill value={active ? 'ACTIVE' : 'SELECTED'} />}
            </div>
            <p className="mt-4 text-3xl font-semibold text-slate-950">
              {formatCurrency(plan.price, plan.currency)}
              <span className="ml-1 text-sm font-semibold text-slate-500">/{formatEnum(plan.billingCycle)}</span>
            </p>
            <ul className="mt-4 space-y-2 text-sm leading-6 text-slate-600">
              {plan.features.map((feature) => (
                <li key={feature} className="flex gap-2">
                  <CheckCircle2 className="mt-1 h-4 w-4 flex-none text-emerald-700" aria-hidden="true" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            {onSelect && (
              <SecondaryButton type="button" className="mt-5 w-full" disabled={disabled} onClick={() => onSelect(plan.id)}>
                {selected ? 'Selected Plan' : 'Select Plan'}
              </SecondaryButton>
            )}
            {onAction && (
              <PrimaryButton type="button" className="mt-5 w-full" disabled={disabled || active} onClick={() => onAction(plan)}>
                {actionLabel ? actionLabel(plan) : active ? 'Active Plan' : 'Pay With Wallet'}
              </PrimaryButton>
            )}
          </article>
        );
      })}
    </div>
  );
};

const LinkCard: React.FC<{
  to: string;
  title: string;
  description: string;
  icon: React.ElementType;
}> = ({ to, title, description, icon: Icon }) => (
  <Link to={to} className="group flex min-h-[184px] flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-[0_18px_48px_-38px_rgba(15,23,42,0.3)] transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_24px_58px_-40px_rgba(4,120,87,0.5)] sm:p-5">
    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
      <Icon className="h-5 w-5" aria-hidden="true" />
    </span>
    <h2 className="mt-4 text-lg font-semibold leading-6 text-slate-950">{title}</h2>
    <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
    <span className="mt-auto inline-flex items-center gap-2 pt-4 text-sm font-semibold text-emerald-700">Open</span>
  </Link>
);

const StandCard: React.FC<{ stand: any }> = ({ stand }) => {
  const mapUrl = buildGoogleMapsUrl(stand.location);

  return (
    <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-[0_18px_46px_-38px_rgba(15,23,42,0.28)] sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase text-emerald-700">
            <Building2 className="h-4 w-4" aria-hidden="true" />
            {formatEnum(stand.type)}
          </div>
          <h3 className="mt-2 text-lg font-semibold text-slate-950">{stand.name}</h3>
          <p className="mt-2 flex items-start gap-2 text-sm leading-6 text-slate-600">
            <MapPin className="mt-0.5 h-4 w-4 flex-none text-slate-400" />
            {[stand.location?.address, stand.location?.village, stand.location?.mandal].filter(Boolean).join(', ') || 'Location pending'}
          </p>
        </div>
        <StatusPill value={stand.verificationStatus} />
      </div>
      {mapUrl && (
        <a
          href={mapUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50"
        >
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
          Open Map
        </a>
      )}
    </article>
  );
};

const useRidesAccountFlow = () => {
  const { user, register, verifyRegistrationOtp, requestRidesAccountSwitchOtp, verifyRidesAccountSwitchOtp } = useAuth();
  const signedInUser: any = (user as any)?.user ? (user as any).user : user;
  const isRidesAccount = String(signedInUser?.role || '').toUpperCase() === 'RIDES';
  const currentUser = isRidesAccount ? signedInUser : null;
  const signedInNonRidesUser = signedInUser && !isRidesAccount ? signedInUser : null;
  const [accountPayload, setAccountPayload] = useState<RidesAccountPayload>(createAccountPayload);
  const [registrationIdentifier, setRegistrationIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [awaitingOtp, setAwaitingOtp] = useState(false);
  const [otpMode, setOtpMode] = useState<'register' | 'rides_switch'>('register');
  const [switchPromptOpen, setSwitchPromptOpen] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [switchError, setSwitchError] = useState('');

  const updateAccount = (patch: Partial<RidesAccountPayload>) =>
    setAccountPayload((current) => ({ ...current, ...patch }));

  const validateAccount = () => {
    if (!accountPayload.firstName.trim() || !accountPayload.lastName.trim() || !accountPayload.username.trim() || !accountPayload.phone.trim()) {
      throw new Error('First name, last name, username and phone are required to create your LocalAlly Rides account.');
    }
    const normalizedPhone = normalizePhone(accountPayload.phone);
    if (!/^\+[1-9][0-9]{7,14}$/.test(normalizedPhone)) {
      throw new Error('Phone must include country code, for example +919876543210.');
    }
    if (accountPayload.password !== accountPayload.confirmPassword) {
      throw new Error('LocalAlly Rides account passwords do not match.');
    }
    if (accountPayload.password.length < 8) {
      throw new Error('Password must be at least 8 characters.');
    }
    return normalizedPhone;
  };

  const confirmRidesAccountSwitch = async () => {
    setSwitchError('');
    setSwitchLoading(true);
    try {
      const normalizedPhone = validateAccount();
      const response = await requestRidesAccountSwitchOtp(normalizedPhone);
      setRegistrationIdentifier(response?.identifier || normalizedPhone);
      setDevOtp(response?.otp || null);
      setOtp('');
      setOtpMode('rides_switch');
      setAwaitingOtp(true);
      setSwitchPromptOpen(false);
    } catch (error: any) {
      setSwitchError(resolveApiErrorMessage(error, 'Unable to send LocalAlly Rides switch OTP.'));
    } finally {
      setSwitchLoading(false);
    }
  };

  const submitWithRidesAccount = async (
    submitRidesRecord: () => Promise<void>,
  ): Promise<'otp_sent' | 'switch_prompt' | 'submitted' | 'submitted_after_registration' | 'submitted_after_switch'> => {
    if (currentUser) {
      await submitRidesRecord();
      return 'submitted';
    }

    if (awaitingOtp) {
      if (!otp.trim()) throw new Error('Enter the OTP sent to your phone before submitting LocalAlly Rides registration.');
      if (otpMode === 'rides_switch') {
        await verifyRidesAccountSwitchOtp({
          identifier: registrationIdentifier,
          otp: otp.trim(),
          firstName: accountPayload.firstName.trim(),
          lastName: accountPayload.lastName.trim(),
          phone: normalizePhone(accountPayload.phone),
        });
        await submitRidesRecord();
        return 'submitted_after_switch';
      }
      await verifyRegistrationOtp(registrationIdentifier, otp.trim());
      await submitRidesRecord();
      return 'submitted_after_registration';
    }

    const normalizedPhone = validateAccount();
    try {
      const response = await register({
        firstName: accountPayload.firstName.trim(),
        lastName: accountPayload.lastName.trim(),
        username: accountPayload.username.trim(),
        password: accountPayload.password,
        role: 'RIDES',
        phone: normalizedPhone,
        ...(accountPayload.email.trim() ? { email: accountPayload.email.trim() } : {}),
      });
      setRegistrationIdentifier(response?.identifier || normalizedPhone);
      setDevOtp(response?.otp || null);
      setOtp('');
      setOtpMode('register');
      setAwaitingOtp(true);
      return 'otp_sent';
    } catch (error) {
      if (isAccountConflictError(error)) {
        setSwitchPromptOpen(true);
        setSwitchError('');
        return 'switch_prompt';
      }
      throw error;
    }
  };

  return {
    currentUser,
    signedInNonRidesUser,
    needsAccountRegistration: !currentUser,
    accountPayload,
    updateAccount,
    awaitingOtp,
    otp,
    setOtp,
    devOtp,
    otpMode,
    switchPromptOpen,
    switchLoading,
    switchError,
    setSwitchPromptOpen,
    confirmRidesAccountSwitch,
    submitWithRidesAccount,
  };
};

const RidesAccountPanel: React.FC<{
  flow: ReturnType<typeof useRidesAccountFlow>;
  title: string;
}> = ({ flow, title }) => {
  const nextPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (!flow.needsAccountRegistration) {
    const ridesRoles = Array.isArray(flow.currentUser?.ridesRoles) ? flow.currentUser.ridesRoles : [];
    return (
      <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-950">
        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-emerald-700" />
        <div>
          <p className="font-semibold">Signed in as {flow.currentUser?.firstName || flow.currentUser?.username || 'LocalAlly user'}</p>
          <p className="mt-1 text-emerald-900">
            This rides record will be linked to your account.
            {ridesRoles.length ? ` Rides roles: ${ridesRoles.map(formatEnum).join(', ')}.` : ''}
          </p>
        </div>
      </div>
    );
  }

  if (flow.awaitingOtp) {
    return (
      <div className="space-y-4 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 flex-none text-amber-700" />
          <div>
            <h2 className="text-base font-semibold text-amber-950">Verify LocalAlly Rides account</h2>
            <p className="mt-1 text-sm text-amber-900">
              {flow.otpMode === 'rides_switch'
                ? 'Enter the OTP to switch your existing account to LocalAlly Rides, then submit this registration.'
                : 'Enter the OTP to activate your LocalAlly Rides account, then submit this registration.'}
              {flow.devOtp ? ` Dev OTP: ${flow.devOtp}` : ''}
            </p>
          </div>
        </div>
        <Field label="OTP" value={flow.otp} required onChange={flow.setOtp} />
      </div>
    );
  }

  return (
    <div className="space-y-5 rounded-lg border border-emerald-200 bg-[linear-gradient(180deg,_#ecfdf5_0%,_#ffffff_100%)] p-4 sm:p-5">
      {flow.switchPromptOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 text-slate-900 shadow-2xl">
            <h2 className="text-lg font-semibold">Account already exists</h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              A LocalAlly account already exists with this phone. Switch that account to LocalAlly Rides and continue?
            </p>
            {flow.switchError && <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{flow.switchError}</div>}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <SecondaryButton type="button" disabled={flow.switchLoading} onClick={() => flow.setSwitchPromptOpen(false)}>No</SecondaryButton>
              <PrimaryButton type="button" disabled={flow.switchLoading} onClick={() => void flow.confirmRidesAccountSwitch()}>
                {flow.switchLoading ? 'Sending OTP...' : 'Yes'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-emerald-700 text-white">
          <UserPlus className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-base font-semibold text-emerald-950">{title}</h2>
          <p className="mt-1 text-sm leading-6 text-emerald-900">
            Create a LocalAlly Rides account for rides only. Driver, passenger and stand roles are linked after OTP verification.
          </p>
        </div>
      </div>
      {flow.signedInNonRidesUser && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-6 text-amber-900">
          You are signed in as {formatEnum(flow.signedInNonRidesUser.role)}. OTP verification here will switch you to the rides workspace.
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="First Name" value={flow.accountPayload.firstName} required onChange={(value) => flow.updateAccount({ firstName: value })} />
        <Field label="Last Name" value={flow.accountPayload.lastName} required onChange={(value) => flow.updateAccount({ lastName: value })} />
        <Field label="Username" value={flow.accountPayload.username} required onChange={(value) => flow.updateAccount({ username: value })} />
        <Field label="Phone" value={flow.accountPayload.phone} required placeholder="+919876543210" onChange={(value) => flow.updateAccount({ phone: value })} />
        <Field label="Email" type="email" value={flow.accountPayload.email} onChange={(value) => flow.updateAccount({ email: value })} />
        <PasswordField label="Password" value={flow.accountPayload.password} required onChange={(value) => flow.updateAccount({ password: value })} />
        <PasswordField label="Confirm Password" value={flow.accountPayload.confirmPassword} required onChange={(value) => flow.updateAccount({ confirmPassword: value })} />
      </div>
      <p className="text-sm text-emerald-900">
        Already have an account? <Link className="font-semibold text-emerald-800 underline" to={`/login?next=${encodeURIComponent(nextPath)}`}>Sign in and continue LocalAlly Rides registration</Link>.
      </p>
    </div>
  );
};

export const RidesHomePage: React.FC = () => {
  const [overview, setOverview] = useState<any>(null);

  useEffect(() => {
    ridesService.overview().then(setOverview).catch(() => setOverview(null));
  }, []);

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f8fafc_0%,_#eefdf5_42%,_#f8fafc_100%)] text-slate-950">
      <SEO title="LocalAlly Rides | Local Autos, Bike Taxis and Stands" description="Book nearby autos, bike taxis, cabs and local stand-based rides through LocalAlly Rides." path="/rides" image={RIDES_HERO_IMAGE} schema={ridesSchema} />
      <section
        className="relative border-b border-slate-200 bg-slate-950 bg-cover bg-center text-white"
        style={{ backgroundImage: `linear-gradient(90deg, rgba(2,6,23,0.9), rgba(6,78,59,0.72), rgba(14,116,144,0.35)), url(${RIDES_HERO_IMAGE})` }}
      >
        <div className="mx-auto grid min-h-[430px] max-w-7xl items-center gap-8 px-4 py-10 sm:px-6 sm:py-12 lg:grid-cols-[minmax(0,1fr)_380px] lg:px-8">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase text-emerald-100 backdrop-blur">
              <Car className="h-4 w-4" /> Local transport
            </p>
            <h1 className="mt-4 max-w-4xl text-4xl font-semibold leading-tight sm:text-6xl">LocalAlly Rides</h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-slate-100">
              Book nearby autos, bike taxis, cabs and stand-based rides while helping existing local transport providers come online.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/rides/book" className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg shadow-slate-950/20 transition hover:bg-emerald-50 sm:w-auto">
                <Navigation className="h-4 w-4" /> Book Ride
              </Link>
              <Link to="/rides/driver/register" className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg border border-white/70 bg-white/10 px-5 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/15 sm:w-auto">
                <Car className="h-4 w-4" /> Register Driver
              </Link>
            </div>
          </div>
          <div className="rounded-lg border border-white/20 bg-white/10 p-4 backdrop-blur sm:p-5">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-2xl font-semibold leading-8">{overview?.activeDrivers || 0}</p>
                <p className="mt-1 text-xs font-semibold uppercase text-emerald-100">Drivers</p>
              </div>
              <div>
                <p className="text-2xl font-semibold leading-8">{overview?.nearbyStands?.length || 0}</p>
                <p className="mt-1 text-xs font-semibold uppercase text-emerald-100">Stands</p>
              </div>
              <div>
                <p className="text-2xl font-semibold leading-8">{overview?.approvedVehicles || 0}</p>
                <p className="mt-1 text-xs font-semibold uppercase text-emerald-100">Vehicles</p>
              </div>
            </div>
            <div className="mt-5 space-y-3 border-t border-white/20 pt-5 text-sm leading-6 text-slate-100">
              <p className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-200" /> Auto, bike, cab and van categories</p>
              <p className="flex items-center gap-2"><Building2 className="h-4 w-4 text-emerald-200" /> Stand registration and queue-ready model</p>
              <p className="flex items-center gap-2"><Wallet className="h-4 w-4 text-cyan-200" /> Cash, UPI and QR-ready records</p>
            </div>
          </div>
        </div>
      </section>

      <section className={sectionClass('max-w-7xl', '-mt-4 relative')}>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <LinkCard to="/rides/book" title="Book a Ride" description="Request an auto, bike, cab, shared ride or scheduled local transport." icon={Navigation} />
          <LinkCard to="/rides/register" title="Rides Registration" description="Create a separate rides account for passenger, driver or stand manager work." icon={UserPlus} />
          <LinkCard to="/rides/driver/register" title="Register Driver" description="Submit driver details, service areas, vehicle category and payout details." icon={Car} />
          <LinkCard to="/rides/stand/register" title="Register Stand" description="Digitize an existing auto, bike, cab or shared transport stand." icon={Building2} />
          <LinkCard to="/rides/stands" title="Nearby Stands" description="Find approved local transport stands and pickup points." icon={Search} />
          <LinkCard to="/rides/dashboard" title="Rides Dashboard" description="Manage ride history, driver requests, vehicles and availability." icon={Activity} />
        </div>
      </section>
    </div>
  );
};

export const RidesRegistrationPage: React.FC = () => (
  <PageShell title="Rides Registration" description="Ride booking is open to every signed-in LocalAlly user. Driver and stand onboarding still uses LocalAlly Rides registration." path="/rides/register">
    <section className={sectionClass()}>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <LinkCard to="/rides/book" title="Book as Any User" description="Sign in with any LocalAlly account before requesting an auto, bike, cab or shared ride." icon={Navigation} />
        <LinkCard to="/rides/driver/register" title="Driver Registration" description="Onboard auto, bike, cab, van or shared transport drivers." icon={Car} />
        <LinkCard to="/rides/stand/register" title="Stand Registration" description="Register existing local stands and transport pickup points." icon={Building2} />
      </div>
    </section>
  </PageShell>
);

const PassengerRideAccessPanel: React.FC<{
  user: any;
  nextPath: string;
}> = ({ user, nextPath }) => {
  if (user) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-950">
        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-emerald-700" />
        <div>
          <p className="font-semibold">Signed in as {user.firstName || user.username || 'LocalAlly user'}</p>
          <p className="mt-1 text-emerald-900">
            This ride request will be linked to your {formatEnum(user.role)} account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950 sm:flex-row sm:items-center sm:justify-between sm:p-5">
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-5 w-5 flex-none text-amber-700" />
        <div>
          <h2 className="text-base font-semibold text-amber-950">Sign in to book</h2>
          <p className="mt-1 text-amber-900">Any LocalAlly account can request a ride.</p>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Link
          to={`/login?next=${encodeURIComponent(nextPath)}`}
          className="inline-flex min-h-[42px] items-center justify-center rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800"
        >
          Sign In
        </Link>
        <Link
          to="/register"
          className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-950 transition hover:bg-amber-100"
        >
          Create Account
        </Link>
      </div>
    </div>
  );
};

export const BookRidePage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const passengerUser: any = (user as any)?.user ? (user as any).user : user;
  const nextPath = `${location.pathname}${location.search}${location.hash}`;
  const [payload, setPayload] = useState<CreateRidePayload>({
    category: 'AUTO',
    pickup: createLocation(),
    destination: createLocation(),
    paymentMode: 'CASH',
    passengerCount: 1,
  });
  const [estimate, setEstimate] = useState<any>(null);
  const [message, setMessage] = useState<MessageState>(null);

  const update = (patch: Partial<CreateRidePayload>) => setPayload((current) => ({ ...current, ...patch }));
  const routeMapUrl = buildGoogleDirectionsUrl(payload.pickup, payload.destination);

  const runEstimate = async () => {
    const data = await ridesService.estimate({
      category: payload.category,
      pickup: payload.pickup,
      destination: payload.destination,
      pricingMode: payload.pricingMode,
    });
    setEstimate(data);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    try {
      if (!passengerUser) {
        setMessage({ type: 'error', text: 'Sign in with any LocalAlly account to book a ride.' });
        return;
      }
      await runEstimate();
      const createdRide = await ridesService.createRide(payload);
      setMessage({
        type: 'success',
        text: `Ride request created. Ride code: ${createdRide?.rideCode}. Start OTP: ${createdRide?.startOtp}`,
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to create ride request.') });
    }
  };

  return (
    <PageShell title="Book Local Ride" description="Request nearby autos, bike taxis, cabs or stand-based local rides." path="/rides/book">
      <section className={sectionClass('max-w-5xl')}>
        <form onSubmit={submit} className={panelClass('space-y-5')}>
          <Message state={message} />
          <PassengerRideAccessPanel user={passengerUser} nextPath={nextPath} />
          <div className="grid gap-4 md:grid-cols-3">
            <SelectField label="Ride Type" value={payload.category} options={rideCategoryOptions} required onChange={(value) => update({ category: value as RideCategory })} />
            <SelectField label="Payment" value={payload.paymentMode || 'CASH'} options={paymentOptions} onChange={(value) => update({ paymentMode: value as PaymentMode })} />
            <Field label="Passengers" type="number" value={String(payload.passengerCount || 1)} onChange={(value) => update({ passengerCount: parseNumber(value) || 1 })} />
          </div>
          <LocationFields prefix="Pickup" value={payload.pickup} required onChange={(pickup) => update({ pickup })} />
          <LocationFields prefix="Destination" value={payload.destination} required onChange={(destination) => update({ destination })} />
          {routeMapUrl && (
            <a
              href={routeMapUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100 sm:w-auto"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              Open Route Map
            </a>
          )}
          <TextAreaField label="Passenger Note" value={payload.passengerNote || ''} onChange={(passengerNote) => update({ passengerNote })} />
          {estimate && (
            <div className="grid gap-3 sm:grid-cols-4">
              <Stat title="Distance" value={`${estimate.estimatedDistanceKm} km`} />
              <Stat title="Duration" value={`${estimate.estimatedDurationMinutes} min`} />
              <Stat title="Estimate" value={`INR ${estimate.estimatedFare}`} />
              <Stat title="Range" value={`INR ${estimate.minimumFare}-${estimate.maximumFare}`} />
            </div>
          )}
          <div className="flex flex-col gap-3 sm:flex-row">
            <SecondaryButton type="button" onClick={() => void runEstimate()} className="w-full sm:w-auto"><Wallet className="h-4 w-4" /> Estimate Fare</SecondaryButton>
            <PrimaryButton type="submit" className="w-full sm:w-auto">
              <Navigation className="h-4 w-4" />
              Request Ride
            </PrimaryButton>
          </div>
        </form>
      </section>
    </PageShell>
  );
};

export const RegisterDriverPage: React.FC = () => {
  const accountFlow = useRidesAccountFlow();
  const [existingDriver, setExistingDriver] = useState<any>(null);
  const [existingVehicles, setExistingVehicles] = useState<any[]>([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [payload, setPayload] = useState<RegisterRideDriverPayload>({
    fullName: '',
    mobileNumber: '',
    categories: ['AUTO'],
    currentAddress: createLocation(),
    serviceAreas: [],
  });
  const [driverPlans, setDriverPlans] = useState<RideDriverPlan[]>([]);
  const [vehicle, setVehicle] = useState<CreateRideVehiclePayload>({
    vehicleType: 'AUTO',
    registrationNumber: '',
    acAvailable: false,
    accessibilitySupport: false,
  });
  const [driverDocuments, setDriverDocuments] = useState<Partial<Record<DriverDocumentKey, File>>>({});
  const [message, setMessage] = useState<MessageState>(null);
  const existingDocumentSummary = existingDriver?.documentSummary || {};
  const isUpdateMode = Boolean(existingDriver);

  useEffect(() => {
    ridesService.driverPlans()
      .then((plans) => {
        setDriverPlans(Array.isArray(plans) ? plans : []);
        const firstPlan = Array.isArray(plans) ? plans[0] : null;
        if (firstPlan) {
          setPayload((current) => ({ ...current, planId: current.planId || firstPlan.id }));
        }
      })
      .catch(() => setDriverPlans([]));
  }, []);

  useEffect(() => {
    if (!accountFlow.currentUser) return;
    setPayload((current) => ({
      ...current,
      fullName: current.fullName || `${accountFlow.currentUser.firstName || ''} ${accountFlow.currentUser.lastName || ''}`.trim(),
      mobileNumber: current.mobileNumber || accountFlow.currentUser.phone || '',
    }));
  }, [accountFlow.currentUser]);

  useEffect(() => {
    if (!accountFlow.currentUser) {
      setExistingDriver(null);
      setExistingVehicles([]);
      return;
    }

    let active = true;
    setProfileLoading(true);
    ridesService.myDriverProfile()
      .then((profileData) => {
        if (!active) return;
        const driver = profileData?.driver || null;
        const vehicles = Array.isArray(profileData?.vehicles) ? profileData.vehicles : [];
        setExistingDriver(driver);
        setExistingVehicles(vehicles);
        if (driver) {
          setPayload({
            fullName: driver.fullName || '',
            mobileNumber: driver.mobileNumber || '',
            alternateMobileNumber: driver.alternateMobileNumber || '',
            categories: Array.isArray(driver.categories) && driver.categories.length ? driver.categories : ['AUTO'],
            currentAddress: driver.currentAddress || createLocation(),
            preferredLanguage: driver.preferredLanguage || '',
            serviceAreas: Array.isArray(driver.serviceAreas) ? driver.serviceAreas : [],
            standId: driver.standId || '',
            upiId: driver.payoutDetails?.upiId || '',
            licenceNumber: driver.documentSummary?.licenceNumber || '',
            identityDocument: driver.documentSummary?.identityDocument || '',
            planId: String(driver.billingSummary?.planId || driverPlans[0]?.id || ''),
          });
        }
      })
      .catch(() => {
        if (!active) return;
        setExistingDriver(null);
        setExistingVehicles([]);
      })
      .finally(() => {
        if (active) setProfileLoading(false);
      });

    return () => {
      active = false;
    };
  }, [accountFlow.currentUser, driverPlans]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    try {
      const result = await accountFlow.submitWithRidesAccount(async () => {
        const missingRequiredDocuments = (['licenceDocument', 'identityDocument'] as DriverDocumentKey[])
          .filter((key) => !driverDocuments[key] && !hasDriverDocument(existingDocumentSummary, key));
        if (missingRequiredDocuments.length) {
          throw new Error('Driving licence and identity proof uploads are required.');
        }
        const oversizedFile = Object.values(driverDocuments).find((file) => file && file.size > 5 * 1024 * 1024);
        if (oversizedFile) {
          throw new Error(`"${oversizedFile.name}" exceeds the 5 MB document upload limit.`);
        }
        const hasNewDocuments = Object.values(driverDocuments).some(Boolean);
        const uploadResult = hasNewDocuments ? await ridesService.uploadDriverDocuments(driverDocuments) : null;
        const documentUrls = uploadResult?.documentUrls || {};
        await ridesService.registerDriver({
          ...payload,
          serviceAreas: String((payload.serviceAreas || []).join(',')).split(',').map((item) => item.trim()).filter(Boolean),
          licenceDocumentUrl: documentUrls.licenceDocument,
          identityDocumentUrl: documentUrls.identityDocument,
          vehicleRcDocumentUrl: documentUrls.vehicleRcDocument,
          vehicleInsuranceDocumentUrl: documentUrls.vehicleInsuranceDocument,
          permitDocumentUrl: documentUrls.permitDocument,
        });
        if (vehicle.registrationNumber.trim()) {
          await ridesService.createVehicle(vehicle);
        }
      });
      if (result === 'switch_prompt') return;
      const updatedProfile = accountFlow.currentUser ? await ridesService.myDriverProfile().catch(() => null) : null;
      if (updatedProfile?.driver) {
        setExistingDriver(updatedProfile.driver);
        setExistingVehicles(Array.isArray(updatedProfile.vehicles) ? updatedProfile.vehicles : []);
      }
      setDriverDocuments({});
      setMessage({
        type: 'success',
        text: result === 'otp_sent'
          ? 'Account OTP sent. Enter OTP and submit again to finish driver registration.'
          : isUpdateMode
            ? 'Driver profile updated and submitted for admin verification. Complete the selected driver plan payment from the dashboard if it is still pending.'
            : 'Driver registration submitted for admin approval. Complete the selected driver plan payment from the dashboard.',
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to register driver.') });
    }
  };

  const toggleCategory = (category: RideCategory, checked: boolean) => {
    setPayload((current) => ({
      ...current,
      categories: checked
        ? Array.from(new Set([...current.categories, category]))
        : current.categories.filter((item) => item !== category),
    }));
  };

  const updateDriverDocument = (key: DriverDocumentKey, file?: File) => {
    setDriverDocuments((current) => {
      const next = { ...current };
      if (file) {
        next[key] = file;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  return (
    <PageShell title={isUpdateMode ? 'Update Rides Driver Profile' : 'Register Rides Driver'} description="Register as an auto, bike, cab, van or shared transport driver for LocalAlly Rides." path="/rides/driver/register">
      <section className={sectionClass('max-w-6xl')}>
        <form onSubmit={submit} className={panelClass('space-y-5')}>
          <Message state={message} />
          <RidesAccountPanel flow={accountFlow} title={isUpdateMode ? 'Rides Driver Account' : 'Create Rides Driver Account'} />
          {profileLoading && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
              Loading existing driver profile...
            </div>
          )}
          {isUpdateMode && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-950">
              <p className="font-semibold">Current verification status: {formatEnum(existingDriver.verificationStatus)}</p>
              <p className="mt-1">Update missing details here. Unapproved profiles remain in the admin verification queue after submit.</p>
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Full Name" value={payload.fullName} required onChange={(fullName) => setPayload((current) => ({ ...current, fullName }))} />
            <Field label="Mobile Number" value={payload.mobileNumber} required onChange={(mobileNumber) => setPayload((current) => ({ ...current, mobileNumber: normalizePhone(mobileNumber) }))} />
            <Field label="Alternate Mobile" value={payload.alternateMobileNumber || ''} onChange={(alternateMobileNumber) => setPayload((current) => ({ ...current, alternateMobileNumber }))} />
            <Field label="Preferred Language" value={payload.preferredLanguage || ''} onChange={(preferredLanguage) => setPayload((current) => ({ ...current, preferredLanguage }))} />
            <Field label="UPI ID" value={payload.upiId || ''} onChange={(upiId) => setPayload((current) => ({ ...current, upiId }))} />
            <Field label="Licence Number" value={payload.licenceNumber || ''} onChange={(licenceNumber) => setPayload((current) => ({ ...current, licenceNumber }))} />
            <Field label="Identity Reference" value={payload.identityDocument || ''} onChange={(identityDocument) => setPayload((current) => ({ ...current, identityDocument }))} />
            <Field label="Service Areas" value={(payload.serviceAreas || []).join(', ')} placeholder="Madhapur, Hitech City, Kondapur" onChange={(value) => setPayload((current) => ({ ...current, serviceAreas: value.split(',').map((item) => item.trim()).filter(Boolean) }))} />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700">Driver Categories</p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {rideCategoryOptions.map((category) => (
                <CheckboxField key={category} label={formatEnum(category)} checked={payload.categories.includes(category)} onChange={(checked) => toggleCategory(category, checked)} />
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start gap-3">
              <Wallet className="mt-1 h-5 w-5 flex-none text-emerald-700" aria-hidden="true" />
              <div>
                <h2 className="text-base font-semibold text-slate-950">Driver Subscription Plan</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Select the plan for this driver profile. Payment can be completed from the rides dashboard before admin approval.
                </p>
              </div>
            </div>
            <div className="mt-4">
              <DriverPlanCards
                plans={driverPlans}
                selectedPlanId={payload.planId}
                activePlanId={existingDriver?.billingSummary?.status === 'ACTIVE' ? String(existingDriver.billingSummary.planId || '') : undefined}
                onSelect={(planId) => setPayload((current) => ({ ...current, planId }))}
              />
            </div>
          </div>
          <LocationFields prefix="Current Address" value={payload.currentAddress} required onChange={(currentAddress) => setPayload((current) => ({ ...current, currentAddress }))} />
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-1 h-5 w-5 flex-none text-emerald-700" aria-hidden="true" />
              <div>
                <h2 className="text-base font-semibold text-slate-950">Driver Documents</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Upload clear photos or PDFs for admin verification. Driving licence and identity proof are required.
                </p>
              </div>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <DocumentUploadField
                label={driverDocumentLabels.licenceDocument}
                file={driverDocuments.licenceDocument}
                existingUrl={existingDocumentSummary.licenceDocumentUrl}
                required
                onChange={(file) => updateDriverDocument('licenceDocument', file)}
              />
              <DocumentUploadField
                label={driverDocumentLabels.identityDocument}
                file={driverDocuments.identityDocument}
                existingUrl={existingDocumentSummary.identityDocumentUrl}
                required
                onChange={(file) => updateDriverDocument('identityDocument', file)}
              />
              <DocumentUploadField
                label={driverDocumentLabels.vehicleRcDocument}
                file={driverDocuments.vehicleRcDocument}
                existingUrl={existingDocumentSummary.vehicleRcDocumentUrl}
                onChange={(file) => updateDriverDocument('vehicleRcDocument', file)}
              />
              <DocumentUploadField
                label={driverDocumentLabels.vehicleInsuranceDocument}
                file={driverDocuments.vehicleInsuranceDocument}
                existingUrl={existingDocumentSummary.vehicleInsuranceDocumentUrl}
                onChange={(file) => updateDriverDocument('vehicleInsuranceDocument', file)}
              />
              <DocumentUploadField
                label={driverDocumentLabels.permitDocument}
                file={driverDocuments.permitDocument}
                existingUrl={existingDocumentSummary.permitDocumentUrl}
                onChange={(file) => updateDriverDocument('permitDocument', file)}
              />
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h2 className="text-base font-semibold text-slate-950">{existingVehicles.length ? 'Add Another Vehicle' : 'Vehicle Details'}</h2>
            {existingVehicles.length > 0 && (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {existingVehicles.map((existingVehicle) => (
                  <div key={getRecordId(existingVehicle) || existingVehicle.registrationNumber} className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-950">{existingVehicle.registrationNumber}</p>
                        <p className="mt-1 text-slate-600">{formatEnum(existingVehicle.vehicleType)} {[existingVehicle.manufacturer, existingVehicle.model].filter(Boolean).join(' ')}</p>
                      </div>
                      <StatusPill value={existingVehicle.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <SelectField label="Vehicle Type" value={vehicle.vehicleType} options={rideCategoryOptions} onChange={(value) => setVehicle((current) => ({ ...current, vehicleType: value as RideCategory }))} />
              <Field label="Registration Number" value={vehicle.registrationNumber} onChange={(registrationNumber) => setVehicle((current) => ({ ...current, registrationNumber }))} />
              <Field label="Manufacturer" value={vehicle.manufacturer || ''} onChange={(manufacturer) => setVehicle((current) => ({ ...current, manufacturer }))} />
              <Field label="Model" value={vehicle.model || ''} onChange={(model) => setVehicle((current) => ({ ...current, model }))} />
              <Field label="Color" value={vehicle.color || ''} onChange={(color) => setVehicle((current) => ({ ...current, color }))} />
              <Field label="Seats" type="number" value={vehicle.seatingCapacity ? String(vehicle.seatingCapacity) : ''} onChange={(value) => setVehicle((current) => ({ ...current, seatingCapacity: parseNumber(value) }))} />
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <CheckboxField label="AC Available" checked={Boolean(vehicle.acAvailable)} onChange={(acAvailable) => setVehicle((current) => ({ ...current, acAvailable }))} />
              <CheckboxField label="Accessibility Support" checked={Boolean(vehicle.accessibilitySupport)} onChange={(accessibilitySupport) => setVehicle((current) => ({ ...current, accessibilitySupport }))} />
            </div>
          </div>
          <PrimaryButton type="submit" className="w-full sm:w-auto"><Car className="h-4 w-4" /> {isUpdateMode ? 'Update Driver Profile' : 'Submit Driver Registration'}</PrimaryButton>
        </form>
      </section>
    </PageShell>
  );
};

export const RegisterStandPage: React.FC = () => {
  const accountFlow = useRidesAccountFlow();
  const [payload, setPayload] = useState<CreateRideStandPayload>({
    name: '',
    type: 'AUTO_STAND',
    location: createLocation(),
    contactPerson: '',
    contactNumber: '',
    queueModel: 'OPEN_ACCEPTANCE',
  });
  const [message, setMessage] = useState<MessageState>(null);

  useEffect(() => {
    if (!accountFlow.currentUser) return;
    setPayload((current) => ({
      ...current,
      contactPerson: current.contactPerson || `${accountFlow.currentUser.firstName || ''} ${accountFlow.currentUser.lastName || ''}`.trim(),
      contactNumber: current.contactNumber || accountFlow.currentUser.phone || '',
    }));
  }, [accountFlow.currentUser]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    try {
      const result = await accountFlow.submitWithRidesAccount(async () => {
        await ridesService.createStand(payload);
      });
      if (result === 'switch_prompt') return;
      setMessage({
        type: 'success',
        text: result === 'otp_sent'
          ? 'Account OTP sent. Enter OTP and submit again to register this stand.'
          : 'Stand registration submitted for admin approval.',
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to register stand.') });
    }
  };

  return (
    <PageShell title="Register Transport Stand" description="Digitize an existing auto, bike, cab or shared transport stand." path="/rides/stand/register">
      <section className={sectionClass('max-w-5xl')}>
        <form onSubmit={submit} className={panelClass('space-y-5')}>
          <Message state={message} />
          <RidesAccountPanel flow={accountFlow} title="Create Rides Stand Manager Account" />
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Stand Name" value={payload.name} required onChange={(name) => setPayload((current) => ({ ...current, name }))} />
            <SelectField label="Stand Type" value={payload.type} options={standTypeOptions} required onChange={(type) => setPayload((current) => ({ ...current, type: type as StandType }))} />
            <Field label="Contact Person" value={payload.contactPerson} required onChange={(contactPerson) => setPayload((current) => ({ ...current, contactPerson }))} />
            <Field label="Contact Number" value={payload.contactNumber} required onChange={(contactNumber) => setPayload((current) => ({ ...current, contactNumber: normalizePhone(contactNumber) }))} />
            <SelectField label="Queue Model" value={payload.queueModel || 'OPEN_ACCEPTANCE'} options={queueOptions} onChange={(queueModel) => setPayload((current) => ({ ...current, queueModel: queueModel as StandQueueModel }))} />
            <Field label="Approx Driver Count" type="number" value={payload.approximateDriverCount ? String(payload.approximateDriverCount) : ''} onChange={(value) => setPayload((current) => ({ ...current, approximateDriverCount: parseNumber(value) }))} />
          </div>
          <LocationFields prefix="Stand" value={payload.location} required onChange={(location) => setPayload((current) => ({ ...current, location }))} />
          <TextAreaField label="Stand Rules" value={payload.rules || ''} onChange={(rules) => setPayload((current) => ({ ...current, rules }))} />
          <PrimaryButton type="submit" className="w-full sm:w-auto"><Building2 className="h-4 w-4" /> Submit Stand</PrimaryButton>
        </form>
      </section>
    </PageShell>
  );
};

export const RidesStandsPage: React.FC = () => {
  const [q, setQ] = useState('');
  const [stands, setStands] = useState<any[]>([]);

  const load = useCallback(async () => {
    const data = await ridesService.stands(q);
    setStands(Array.isArray(data) ? data : []);
  }, [q]);

  useEffect(() => {
    void load().catch(() => setStands([]));
  }, [load]);

  return (
    <PageShell title="Nearby Ride Stands" description="Find approved local auto, bike, cab and shared transport stands." path="/rides/stands">
      <section className={sectionClass()}>
        <form onSubmit={(event) => { event.preventDefault(); void load(); }} className={panelClass('grid gap-4 md:grid-cols-[1fr_auto]')}>
          <Field label="Search Area" value={q} onChange={setQ} />
          <div className="flex items-end">
            <PrimaryButton type="submit" className="w-full"><Search className="h-4 w-4" /> Search</PrimaryButton>
          </div>
        </form>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {stands.map((stand) => <StandCard key={getRecordId(stand)} stand={stand} />)}
        </div>
        {!stands.length && <div className="mt-8 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm sm:p-5">No approved stands found.</div>}
      </section>
    </PageShell>
  );
};

export const RidesDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [requests, setRequests] = useState<any[]>([]);
  const [driverPlans, setDriverPlans] = useState<RideDriverPlan[]>([]);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [message, setMessage] = useState<MessageState>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [rideAlert, setRideAlert] = useState<any>(null);
  const [requestsRefreshing, setRequestsRefreshing] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [pushAlertStatus, setPushAlertStatus] = useState<'idle' | 'enabled' | 'disabled' | 'unsupported' | 'error'>('idle');
  const knownRequestIdsRef = useRef<Set<string>>(new Set());
  const requestsHydratedRef = useRef(false);
  const ridesSocketRef = useRef<Socket | null>(null);
  const [availabilityForm, setAvailabilityForm] = useState({
    currentLocation: createLocation(),
    availabilityNote: '',
    availabilityValidUntil: toDateTimeLocalValue(new Date(Date.now() + 4 * 60 * 60 * 1000)),
  });

  const notifyDriverOfRide = useCallback((ride: any) => {
    setRideAlert(ride);
    if (typeof window === 'undefined' || !('Notification' in window)) return;

    const title = 'New nearby ride request';
    const body = [
      `${formatEnum(ride.category)} ride`,
      ride.pickup?.address && ride.destination?.address ? `${ride.pickup.address} to ${ride.destination.address}` : '',
      Number.isFinite(Number(ride.dispatchDistanceKm)) ? `${Number(ride.dispatchDistanceKm).toFixed(1)} km from you` : '',
      ride.estimatedFare ? `Estimate ${formatCurrency(ride.estimatedFare)}` : '',
    ].filter(Boolean).join(' | ');

    const showNotification = () => {
      try {
        new Notification(title, { body });
      } catch {
        // Browser notification support varies; the in-app alert remains the primary signal.
      }
    };

    if (Notification.permission === 'granted') {
      showNotification();
    } else if (Notification.permission === 'default') {
      void Notification.requestPermission().then((permission) => {
        if (permission === 'granted') showNotification();
      });
    }
  }, []);

  const applyDriverRequests = useCallback((incoming: any[], notifyNew: boolean) => {
    const normalizedRequests = Array.isArray(incoming) ? incoming : [];
    const previousIds = knownRequestIdsRef.current;
    const nextIds = new Set(normalizedRequests.map(getRecordId).filter(Boolean));
    const newRide = normalizedRequests.find((ride) => {
      const id = getRecordId(ride);
      return id && !previousIds.has(id);
    });

    setRequests(normalizedRequests);
    knownRequestIdsRef.current = nextIds;

    if (!requestsHydratedRef.current) {
      requestsHydratedRef.current = true;
      return;
    }

    if (notifyNew && newRide) {
      notifyDriverOfRide(newRide);
    }
  }, [notifyDriverOfRide]);

  const mergeIncomingDriverRide = useCallback((ride: any) => {
    const rideId = getRecordId(ride);
    if (!rideId) return;

    const alreadyKnown = knownRequestIdsRef.current.has(rideId);
    knownRequestIdsRef.current = new Set([...Array.from(knownRequestIdsRef.current), rideId]);
    requestsHydratedRef.current = true;
    setRequests((current) => {
      const existingIndex = current.findIndex((item) => getRecordId(item) === rideId);
      const next = existingIndex >= 0
        ? current.map((item, index) => (index === existingIndex ? ride : item))
        : [ride, ...current];

      return next.sort((left, right) => {
        const leftDistance = Number(left.dispatchDistanceKm);
        const rightDistance = Number(right.dispatchDistanceKm);
        const leftHasDistance = Number.isFinite(leftDistance);
        const rightHasDistance = Number.isFinite(rightDistance);
        if (leftHasDistance && rightHasDistance && leftDistance !== rightDistance) return leftDistance - rightDistance;
        if (leftHasDistance !== rightHasDistance) return leftHasDistance ? -1 : 1;
        return new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime();
      });
    });

    if (!alreadyKnown) {
      notifyDriverOfRide(ride);
    }
  }, [notifyDriverOfRide]);

  const enableDriverPushAlerts = useCallback(async () => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      setPushAlertStatus('unsupported');
      setMessage({ type: 'error', text: 'Browser push notifications are not supported here.' });
      return;
    }

    try {
      setPushAlertStatus('idle');
      const config = await ridesService.driverNotificationConfig();
      if (!config?.enabled || !config?.publicKey) {
        setPushAlertStatus('disabled');
        setMessage({ type: 'error', text: 'Native push is not configured on the server. Add VAPID keys to enable background ride alerts.' });
        return;
      }

      if ('Notification' in window && Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          setPushAlertStatus('disabled');
          setMessage({ type: 'error', text: 'Notification permission is required for background ride alerts.' });
          return;
        }
      }

      const registration = await navigator.serviceWorker.register('/rides-push-sw.js');
      const existing = await registration.pushManager.getSubscription();
      const subscription = existing || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.publicKey),
      });
      const serialized = subscription.toJSON() as any;
      if (!serialized?.endpoint || !serialized?.keys?.p256dh || !serialized?.keys?.auth) {
        throw new Error('Push subscription is missing browser keys.');
      }

      await ridesService.subscribeDriverNotifications({
        endpoint: serialized.endpoint,
        keys: {
          p256dh: serialized.keys.p256dh,
          auth: serialized.keys.auth,
        },
        userAgent: navigator.userAgent,
      });
      setPushAlertStatus('enabled');
      setMessage({ type: 'success', text: 'Background ride alerts enabled for this browser.' });
    } catch (error: any) {
      setPushAlertStatus('error');
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to enable background ride alerts.') });
    }
  }, []);

  const load = useCallback(async () => {
    const [profileData, ridesData, requestsData, plansData, walletData] = await Promise.all([
      ridesService.myDriverProfile().catch(() => null),
      ridesService.myRides().catch(() => []),
      ridesService.driverRequests().catch(() => []),
      ridesService.driverPlans().catch(() => []),
      ridesService.walletBalance().catch(() => null),
    ]);
    setProfile(profileData);
    setDriverPlans(Array.isArray(plansData) ? plansData : []);
    setWalletBalance(walletData ? Number(walletData.balance || 0) : null);
    const driver = profileData?.driver;
    if (driver) {
      setAvailabilityForm((current) => ({
        currentLocation: driver.currentLocation || driver.currentAddress || current.currentLocation,
        availabilityNote: driver.availabilityPost?.note || current.availabilityNote,
        availabilityValidUntil: driver.availabilityPost?.validUntil
          ? toDateTimeLocalValue(new Date(driver.availabilityPost.validUntil))
          : current.availabilityValidUntil,
      }));
    }
    setHistory(Array.isArray(ridesData) ? ridesData : []);
    applyDriverRequests(Array.isArray(requestsData) ? requestsData : [], false);
  }, [applyDriverRequests]);

  useEffect(() => {
    void load();
  }, [load]);

  const setAvailability = async (isOnline: boolean) => {
    setMessage(null);
    setAvailabilityLoading(true);
    try {
      const currentLocation = isOnline
        ? await getBrowserRideLocation('Driver current GPS location')
        : undefined;
      if (currentLocation) {
        setAvailabilityForm((current) => ({ ...current, currentLocation }));
      }
      await ridesService.updateAvailability({
        isOnline,
        isAvailable: isOnline,
        ...(currentLocation ? { currentLocation } : {}),
        ...(isOnline && availabilityForm.availabilityNote.trim() ? { availabilityNote: availabilityForm.availabilityNote.trim() } : {}),
        ...(isOnline && availabilityForm.availabilityValidUntil ? { availabilityValidUntil: fromDateTimeLocalValue(availabilityForm.availabilityValidUntil) } : {}),
      });
      setMessage({ type: 'success', text: isOnline ? 'Driver availability set online with current location.' : 'Driver availability set offline.' });
      await load();
    } catch (error: any) {
      const fallback = isOnline
        ? `${getGeolocationMessage(error)} Location permission is required before going online.`
        : 'Unable to update availability.';
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, fallback) });
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const postAvailability = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    setAvailabilityLoading(true);
    try {
      await ridesService.updateAvailability({
        isOnline: true,
        isAvailable: true,
        currentLocation: availabilityForm.currentLocation,
        availabilityNote: availabilityForm.availabilityNote.trim(),
        availabilityValidUntil: fromDateTimeLocalValue(availabilityForm.availabilityValidUntil),
      });
      setMessage({ type: 'success', text: 'Ride availability posted for passengers and admin visibility.' });
      await load();
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to post ride availability.') });
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const payDriverPlan = async (plan: RideDriverPlan) => {
    setMessage(null);
    setBillingLoading(true);
    try {
      await ridesService.subscribeDriverPlan({ planId: plan.id, paymentMethodId: 'WALLET' });
      setMessage({ type: 'success', text: `${plan.name} activated successfully.` });
      await load();
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to activate driver plan from wallet.') });
      await load();
    } finally {
      setBillingLoading(false);
    }
  };

  const topUpForDriverPlan = (plan: RideDriverPlan) => {
    localStorage.setItem(PENDING_RIDES_DRIVER_PLAN_KEY, JSON.stringify({
      planId: plan.id,
      planName: plan.name,
      amount: plan.price,
      currency: plan.currency,
    }));
    navigate('/student/wallet/topup', { state: { amount: plan.price, role: 'RIDES' } });
  };

  const accept = async (rideId: string) => {
    setMessage(null);
    try {
      await ridesService.acceptRide(rideId);
      setRideAlert((current: any) => (getRecordId(current) === rideId ? null : current));
      setMessage({ type: 'success', text: 'Ride accepted.' });
      await load();
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to accept ride.') });
    }
  };

  const driver = profile?.driver;
  const profileVehicles = Array.isArray(profile?.vehicles) ? profile.vehicles : [];
  const isApprovedDriver = driver?.verificationStatus === 'APPROVED';
  const billingSummary = driver?.billingSummary || {};
  const hasActiveDriverPlan = billingSummary.status === 'ACTIVE';
  const activeDriverPlanId = billingSummary.status === 'ACTIVE' ? String(billingSummary.planId || '') : '';
  const selectedDriverPlanId = String(billingSummary.planId || driverPlans[0]?.id || '');
  const driverMapUrl = buildGoogleMapsUrl(driver?.currentLocation || driver?.currentAddress);
  const driverCanReceiveLiveRequests = Boolean(driver?.isOnline && driver?.isAvailable && isApprovedDriver && hasActiveDriverPlan);

  useEffect(() => {
    if (!driverCanReceiveLiveRequests) {
      ridesSocketRef.current?.disconnect();
      ridesSocketRef.current = null;
      setSocketConnected(false);
      return undefined;
    }

    const token = getStoredAccessToken();
    if (!token) return undefined;

    const socket = io(`${RIDES_API_URL}/rides`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
    });

    ridesSocketRef.current = socket;
    socket.on('connect', () => setSocketConnected(true));
    socket.on('disconnect', () => setSocketConnected(false));
    socket.on('rides:request:new', (payload: any) => {
      const ride = payload?.ride || payload;
      mergeIncomingDriverRide(ride);
    });

    return () => {
      socket.disconnect();
      if (ridesSocketRef.current === socket) {
        ridesSocketRef.current = null;
      }
      setSocketConnected(false);
    };
  }, [driverCanReceiveLiveRequests, mergeIncomingDriverRide]);

  useEffect(() => {
    if (!driverCanReceiveLiveRequests) return undefined;

    let active = true;
    const refreshRequests = async () => {
      setRequestsRefreshing(true);
      try {
        const data = await ridesService.driverRequests();
        if (active) applyDriverRequests(Array.isArray(data) ? data : [], true);
      } catch {
        if (active) applyDriverRequests([], false);
      } finally {
        if (active) setRequestsRefreshing(false);
      }
    };

    void refreshRequests();
    const interval = window.setInterval(() => {
      void refreshRequests();
    }, DRIVER_REQUEST_POLL_MS);

    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [applyDriverRequests, driverCanReceiveLiveRequests]);

  return (
    <PageShell title="Rides Dashboard" description="Manage LocalAlly Rides driver profile, vehicles, ride requests and ride history." path="/rides/dashboard" robots="noindex, nofollow">
      <section className={sectionClass()}>
        <Message state={message} />
        {rideAlert && (
          <div className="fixed inset-x-4 top-24 z-[1000] mx-auto max-w-2xl rounded-lg border border-emerald-200 bg-white p-4 shadow-2xl shadow-slate-950/20 sm:p-5" role="alert" aria-live="assertive">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-emerald-700">New nearby ride request</p>
                <h2 className="mt-2 text-xl font-semibold text-slate-950">{formatEnum(rideAlert.category)} ride</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {rideAlert.pickup?.address || 'Pickup pending'} to {rideAlert.destination?.address || 'destination pending'}
                </p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
                  {Number.isFinite(Number(rideAlert.dispatchDistanceKm)) && (
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">
                      {Number(rideAlert.dispatchDistanceKm).toFixed(1)} km away
                    </span>
                  )}
                  {rideAlert.dispatchEstimatedArrivalMinutes && (
                    <span className="rounded-full bg-cyan-50 px-3 py-1 text-cyan-700">
                      ETA {rideAlert.dispatchEstimatedArrivalMinutes} min
                    </span>
                  )}
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                    {rideAlert.estimatedFare ? formatCurrency(rideAlert.estimatedFare) : 'Fare estimate pending'}
                  </span>
                </div>
              </div>
              <div className="grid gap-2 sm:min-w-[150px]">
                <PrimaryButton type="button" onClick={() => void accept(getRecordId(rideAlert))}>
                  Accept Ride
                </PrimaryButton>
                <SecondaryButton type="button" onClick={() => setRideAlert(null)}>
                  Dismiss
                </SecondaryButton>
              </div>
            </div>
          </div>
        )}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
          <Stat title="Driver Status" value={formatEnum(driver?.verificationStatus || 'Not Registered')} />
          <Stat title="Online" value={driver?.isOnline ? 'Yes' : 'No'} />
          <Stat title="Vehicles" value={profileVehicles.length} />
          <Stat title="My Rides" value={history.length} />
        </div>
        <section className={panelClass('mt-8')}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-slate-950">Driver Verification</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Submitted registrations stay here as pending until admin approves the driver and active vehicle.
              </p>
            </div>
            <Link to="/rides/driver/register" className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50">
              Update Missing Details
            </Link>
          </div>
          <DriverVerificationDetails driver={driver} vehicles={profileVehicles} showAvailability />
        </section>
        <section className={panelClass('mt-8 space-y-5')}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-slate-950">Driver Plan & Payment</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Admin approval and online availability require an active LocalAlly Rides driver plan.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
              <p className="text-xs font-semibold uppercase text-slate-500">Wallet Balance</p>
              <p className="mt-1 text-lg font-semibold text-slate-950">
                {walletBalance === null ? 'Unavailable' : formatCurrency(walletBalance)}
              </p>
            </div>
          </div>
          <DriverPlanCards
            plans={driverPlans}
            selectedPlanId={selectedDriverPlanId}
            activePlanId={activeDriverPlanId}
            disabled={billingLoading || !driver}
            actionLabel={(plan) => {
              if (activeDriverPlanId === plan.id) return 'Active Plan';
              return walletBalance !== null && walletBalance >= Number(plan.price || 0) ? 'Pay With Wallet' : `Top Up ${formatCurrency(plan.price, plan.currency)}`;
            }}
            onAction={(plan) => {
              if (walletBalance !== null && walletBalance >= Number(plan.price || 0)) {
                void payDriverPlan(plan);
              } else {
                topUpForDriverPlan(plan);
              }
            }}
          />
        </section>
        <div className="mt-8 flex flex-wrap gap-3">
          <PrimaryButton type="button" disabled={availabilityLoading || !isApprovedDriver || !hasActiveDriverPlan} onClick={() => void setAvailability(true)}>
            <Activity className="h-4 w-4" /> {availabilityLoading ? 'Updating...' : 'Go Online'}
          </PrimaryButton>
          <SecondaryButton type="button" disabled={availabilityLoading || !driver} onClick={() => void setAvailability(false)}>Go Offline</SecondaryButton>
          <SecondaryButton type="button" disabled={!driverCanReceiveLiveRequests} onClick={() => void enableDriverPushAlerts()}>
            <ShieldCheck className="h-4 w-4" /> Enable Background Alerts
          </SecondaryButton>
          {driverMapUrl && (
            <a
              href={driverMapUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              Driver Map
            </a>
          )}
          <Link to="/rides/driver/register" className="inline-flex min-h-[48px] items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-800">Update Driver Profile</Link>
        </div>
        {driver && (!isApprovedDriver || !hasActiveDriverPlan) && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
            Admin approval and an active driver plan are required before going online or accepting ride requests. You can still update profile, document, vehicle, and payment details.
          </div>
        )}
        {driverCanReceiveLiveRequests && (
          <div className="mt-4 grid gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-950 sm:grid-cols-2">
            <p>
              Live dispatch socket: <span className="font-semibold">{socketConnected ? 'Connected' : 'Connecting'}</span>
            </p>
            <p>
              Background alerts: <span className="font-semibold">{formatEnum(pushAlertStatus)}</span>
            </p>
          </div>
        )}
        <form onSubmit={postAvailability} className={panelClass('mt-8 space-y-5')}>
          <div>
            <h2 className="text-2xl font-semibold text-slate-950">Post Ride Availability</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Approved drivers can publish where they are available, how long they can take rides, and any route note.
            </p>
          </div>
          <LocationFields
            prefix="Availability"
            value={availabilityForm.currentLocation}
            required
            onChange={(currentLocation) => setAvailabilityForm((current) => ({ ...current, currentLocation }))}
          />
          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="Available Until"
              type="datetime-local"
              value={availabilityForm.availabilityValidUntil}
              onChange={(availabilityValidUntil) => setAvailabilityForm((current) => ({ ...current, availabilityValidUntil }))}
            />
            <TextAreaField
              label="Availability Note"
              value={availabilityForm.availabilityNote}
              onChange={(availabilityNote) => setAvailabilityForm((current) => ({ ...current, availabilityNote }))}
            />
          </div>
          <PrimaryButton type="submit" disabled={availabilityLoading || !isApprovedDriver || !hasActiveDriverPlan}>
            <Activity className="h-4 w-4" /> {availabilityLoading ? 'Posting...' : 'Post Availability'}
          </PrimaryButton>
        </form>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <section>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-slate-950">Open Ride Requests</h2>
                <p className="mt-1 text-sm text-slate-600">
                  {driverCanReceiveLiveRequests
                    ? `WebSocket alerts are instant. Fallback refresh runs every ${Math.round(DRIVER_REQUEST_POLL_MS / 1000)} seconds.`
                    : 'Go online with an active plan and active vehicle to receive nearby requests.'}
                </p>
              </div>
              {requestsRefreshing && <span className="text-sm font-semibold text-emerald-700">Checking...</span>}
            </div>
            <div className="mt-4 grid gap-4">
              {requests.map((ride) => (
                <article key={getRecordId(ride)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-slate-950">{formatEnum(ride.category)} Ride</h3>
                      <p className="mt-1 text-sm text-slate-600">{ride.pickup?.address} to {ride.destination?.address}</p>
                    </div>
                    <StatusPill value={ride.status} />
                  </div>
                  <p className="mt-3 text-sm font-semibold text-emerald-700">Estimate INR {ride.estimatedFare || '-'}</p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
                    {Number.isFinite(Number(ride.dispatchDistanceKm)) && (
                      <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">
                        {Number(ride.dispatchDistanceKm).toFixed(1)} km from you
                      </span>
                    )}
                    {ride.dispatchEstimatedArrivalMinutes && (
                      <span className="rounded-full bg-cyan-50 px-3 py-1 text-cyan-700">
                        ETA {ride.dispatchEstimatedArrivalMinutes} min
                      </span>
                    )}
                    {ride.dispatchMatchReason && (
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                        {ride.dispatchMatchReason}
                      </span>
                    )}
                  </div>
                  <PrimaryButton type="button" className="mt-4 w-full sm:w-auto" onClick={() => void accept(getRecordId(ride))}>Accept Ride</PrimaryButton>
                </article>
              ))}
              {!requests.length && <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">No open ride requests right now.</div>}
            </div>
          </section>
          <section>
            <h2 className="text-2xl font-semibold text-slate-950">Ride History</h2>
            <div className="mt-4 grid gap-4">
              {history.slice(0, 6).map((ride) => (
                <article key={getRecordId(ride)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-slate-950">{ride.rideCode}</h3>
                      <p className="mt-1 text-sm text-slate-600">{ride.pickup?.address} to {ride.destination?.address}</p>
                    </div>
                    <StatusPill value={ride.status} />
                  </div>
                </article>
              ))}
              {!history.length && <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">No rides yet.</div>}
            </div>
          </section>
        </div>
      </section>
    </PageShell>
  );
};

export const AdminRidesPage: React.FC = () => {
  const [overview, setOverview] = useState<any>(null);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [stands, setStands] = useState<any[]>([]);
  const [rides, setRides] = useState<any[]>([]);
  const [message, setMessage] = useState<MessageState>(null);

  const load = useCallback(async () => {
    const [overviewData, driversData, vehiclesData, standsData, ridesData] = await Promise.all([
      ridesService.adminOverview(),
      ridesService.adminDrivers(),
      ridesService.adminVehicles(),
      ridesService.adminStands(),
      ridesService.adminRides(),
    ]);
    setOverview(overviewData);
    setDrivers(Array.isArray(driversData) ? driversData : []);
    setVehicles(Array.isArray(vehiclesData) ? vehiclesData : []);
    setStands(Array.isArray(standsData) ? standsData : []);
    setRides(Array.isArray(ridesData) ? ridesData : []);
  }, []);

  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);

  const action = async (work: Promise<any>, success: string) => {
    setMessage(null);
    try {
      await work;
      setMessage({ type: 'success', text: success });
      await load();
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Admin action failed.') });
    }
  };

  return (
    <PageShell title="Admin Rides" description="Approve drivers, vehicles and stands, and monitor LocalAlly Rides operations." path="/admin/rides" robots="noindex, nofollow">
      <section className={sectionClass()}>
        <Message state={message} />
        <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
          {Object.entries(overview || {}).map(([key, value]) => <Stat key={key} title={formatEnum(key)} value={String(value)} />)}
        </div>
        <h2 className="mt-10 text-2xl font-semibold text-slate-950">Pending Drivers</h2>
        <div className="mt-4 grid gap-4">
          {drivers.filter((driver) => ['SUBMITTED', 'UNDER_REVIEW', 'MORE_INFORMATION_REQUIRED'].includes(driver.verificationStatus)).map((driver) => {
            const driverVehicles = vehicles.filter((vehicle) => vehicle.driverId === driver.userId);
            return (
              <article key={getRecordId(driver)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-950">{driver.fullName}</h3>
                    <p className="mt-1 text-sm text-slate-600">{(driver.categories || []).map(formatEnum).join(', ')} | {driver.mobileNumber}</p>
                  </div>
                  <div className="grid gap-2 sm:flex">
                    <PrimaryButton type="button" onClick={() => void action(ridesService.reviewDriver(getRecordId(driver), 'APPROVED'), 'Driver approved.')}>Approve</PrimaryButton>
                    <SecondaryButton type="button" onClick={() => void action(ridesService.reviewDriver(getRecordId(driver), 'MORE_INFORMATION_REQUIRED'), 'More information requested.')}>Need Info</SecondaryButton>
                    <SecondaryButton type="button" onClick={() => void action(ridesService.reviewDriver(getRecordId(driver), 'REJECTED'), 'Driver rejected.')}>Reject</SecondaryButton>
                  </div>
                </div>
                <DriverVerificationDetails driver={driver} vehicles={driverVehicles} showAvailability />
              </article>
            );
          })}
          {!drivers.filter((driver) => ['SUBMITTED', 'UNDER_REVIEW', 'MORE_INFORMATION_REQUIRED'].includes(driver.verificationStatus)).length && (
            <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
              No drivers are pending verification.
            </div>
          )}
        </div>
        <h2 className="mt-10 text-2xl font-semibold text-slate-950">Active Driver Availability</h2>
        <div className="mt-4 grid gap-4">
          {drivers.filter((driver) => driver.isOnline || driver.isAvailable || driver.availabilityPost).map((driver) => {
            const driverVehicles = vehicles.filter((vehicle) => vehicle.driverId === driver.userId);
            return (
              <article key={getRecordId(driver)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-950">{driver.fullName}</h3>
                    <p className="mt-1 text-sm text-slate-600">{driver.mobileNumber}</p>
                  </div>
                  <StatusPill value={driver.isOnline ? 'ONLINE' : 'OFFLINE'} />
                </div>
                <DriverVerificationDetails driver={driver} vehicles={driverVehicles} showAvailability />
              </article>
            );
          })}
          {!drivers.filter((driver) => driver.isOnline || driver.isAvailable || driver.availabilityPost).length && (
            <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
              No drivers have posted availability yet.
            </div>
          )}
        </div>
        <h2 className="mt-10 text-2xl font-semibold text-slate-950">All Drivers</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {drivers.map((driver) => (
            <article key={getRecordId(driver)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-slate-950">{driver.fullName}</h3>
                  <p className="mt-1 text-sm text-slate-600">{driver.mobileNumber}</p>
                </div>
                <StatusPill value={driver.verificationStatus} />
              </div>
            </article>
          ))}
        </div>
        <h2 className="mt-10 text-2xl font-semibold text-slate-950">Pending Vehicles</h2>
        <div className="mt-4 grid gap-4">
          {vehicles.filter((vehicle) => vehicle.status === 'PENDING').map((vehicle) => (
            <article key={getRecordId(vehicle)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">{vehicle.registrationNumber}</h3>
                  <p className="mt-1 text-sm text-slate-600">{formatEnum(vehicle.vehicleType)} | {[vehicle.manufacturer, vehicle.model].filter(Boolean).join(' ')}</p>
                </div>
                <PrimaryButton type="button" onClick={() => void action(ridesService.reviewVehicle(getRecordId(vehicle), 'ACTIVE'), 'Vehicle activated.')}>Activate</PrimaryButton>
              </div>
            </article>
          ))}
        </div>
        <h2 className="mt-10 text-2xl font-semibold text-slate-950">Pending Stands</h2>
        <div className="mt-4 grid gap-4">
          {stands.filter((stand) => ['SUBMITTED', 'UNDER_REVIEW'].includes(stand.verificationStatus)).map((stand) => (
            <article key={getRecordId(stand)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">{stand.name}</h3>
                  <p className="mt-1 text-sm text-slate-600">{formatEnum(stand.type)} | {stand.location?.address}</p>
                </div>
                <PrimaryButton type="button" onClick={() => void action(ridesService.reviewStand(getRecordId(stand), 'APPROVED'), 'Stand approved.')}>Approve</PrimaryButton>
              </div>
            </article>
          ))}
        </div>
        <h2 className="mt-10 text-2xl font-semibold text-slate-950">Recent Rides</h2>
        <div className="mt-4 grid gap-4">
          {rides.slice(0, 8).map((ride) => (
            <article key={getRecordId(ride)} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">{ride.rideCode}</h3>
                  <p className="mt-1 text-sm text-slate-600">{ride.pickup?.address} to {ride.destination?.address}</p>
                </div>
                <StatusPill value={ride.status} />
              </div>
            </article>
          ))}
        </div>
      </section>
    </PageShell>
  );
};
